import {
    Button,
    Drawer,
    DrawerProps,
    Group,
    ScrollArea,
    Select,
    Stack,
    TextInput,
} from '@mantine/core';
import {Formik} from 'formik';
import {I18n} from '@aws-amplify/core';

import {
    BankAccountsFilters,
    useBankAccountsFilter,
} from '../hooks/useBankAccountsFilter';

/**
 * FNP-918: the bank accounts filter drawer (§7).
 *
 * Formik-driven, matching the form conventions in §6. Filter state lives in the
 * MODULE-SCOPED store (useBankAccountsFilter), not component state, so the
 * table — a sibling, not a child — filters the same list.
 *
 * ⚠️ Two fields are offered: `accountHolderType` and `country`.
 * `accountHolderType` is a genuine enum (individual / company) and is exactly
 * the kind of low-cardinality value a filter control beats a keyword box for.
 * `country` stays a TextInput because it is a two-letter ISO code with ~200
 * values, and a 200-item dropdown is worse than typing "US".
 *
 * `bankName` and `accountHolderName` are deliberately NOT here — they are free
 * text already covered by the keyword search, and a filter for them would
 * duplicate it.
 *
 * ⚠️ Filtering is CLIENT-SIDE over the loaded window — `listBankAccounts`'
 * filter argument carries only the customerId scope. See useBankAccounts.ts.
 */
export const FilterDrawer = ({
                                 opened,
                                 onClose,
                                 ...rest
                             }: DrawerProps) => {

    const {
        filters,
        handleSetFilters,
        handleClearFilters,
    } = useBankAccountsFilter();

    return (
        <Drawer
            transitionProps={{
                transition: 'slide-left',
                duration: 750,
                timingFunction: 'ease',
            }}
            position='right'
            offset={10}
            opened={opened}
            onClose={onClose}
            title={I18n.get('Filter Bank Accounts')}
            padding='xl'
            size='md'
            scrollAreaComponent={ScrollArea.Autosize}
            {...rest}
        >
            <Formik<BankAccountsFilters>
                initialValues={filters}
                enableReinitialize
                onSubmit={(values) => {
                    handleSetFilters(values);
                    onClose();
                }}
            >
                {({
                      values,
                      setFieldValue,
                      handleSubmit,
                      resetForm,
                  }) => (
                    <form onSubmit={(event) => {
                        event.preventDefault();
                        handleSubmit();
                    }}>
                        <Stack gap='md'>
                            <Select
                                label={I18n.get('Account Holder Type')}
                                placeholder={I18n.get('Any')}
                                clearable
                                data={[
                                    {
                                        value: 'individual',
                                        label: I18n.get('Individual'),
                                    },
                                    {
                                        value: 'company',
                                        label: I18n.get('Company'),
                                    },
                                ]}
                                value={values.accountHolderType ?? null}
                                onChange={(value) =>
                                    setFieldValue(
                                        'accountHolderType',
                                        value ?? '',
                                    )}
                            />

                            <TextInput
                                label={I18n.get('Country')}
                                placeholder={I18n.get('Two-letter code, e.g. US')}
                                value={values.country ?? ''}
                                onChange={(event) =>
                                    setFieldValue(
                                        'country',
                                        event.currentTarget.value,
                                    )}
                            />

                            <Group justify='flex-end' mt='md'>
                                {/*
                                  * Clears EVERY field and is reachable from the
                                  * drawer (§7) — it resets both the store and
                                  * the visible form, so the two cannot disagree.
                                  */}
                                <Button
                                    variant='subtle'
                                    onClick={() => {
                                        handleClearFilters();
                                        resetForm({values: {}});
                                    }}
                                >
                                    {I18n.get('Clear filters')}
                                </Button>
                                <Button type='submit'>
                                    {I18n.get('Apply')}
                                </Button>
                            </Group>
                        </Stack>
                    </form>
                )}
            </Formik>
        </Drawer>
    );
};
